"use client";

import Image from "next/image";
import Link from "next/link";
import { ChevronLeft, ChevronRight, ArrowUpRight } from "lucide-react";
import { useRef } from "react";
import type { ProductSummary } from "@plexstore/types";

const typeLabel: Record<ProductSummary["productType"], string> = {
  digitalPaid: "Digital",
  digitalFree: "Free",
  service: "Service",
  serials: "Serial key",
  account: "Account",
  multiPrice: "Plans",
};

export function ProductCarousel({ products }: { products: ProductSummary[] }) {
  const scroller = useRef<HTMLDivElement>(null);

  const scrollByCard = (direction: -1 | 1) => {
    const node = scroller.current;
    if (!node) return;
    node.scrollBy({ left: direction * 230, behavior: "smooth" });
  };

  if (products.length === 0) return null;

  return (
    <section id="catalog" aria-label="Products" className="relative mx-auto w-full max-w-[1180px] px-14">
      <button
        type="button"
        aria-label="Previous products"
        onClick={() => scrollByCard(-1)}
        className="absolute top-1/2 left-0 z-10 grid size-10 -translate-y-1/2 place-items-center rounded-full border border-white/15 bg-[#07101c]/80 text-white backdrop-blur focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#2f7cff]"
      >
        <ChevronLeft className="size-4" />
      </button>
      <button
        type="button"
        aria-label="Next products"
        onClick={() => scrollByCard(1)}
        className="absolute top-1/2 right-0 z-10 grid size-10 -translate-y-1/2 place-items-center rounded-full border border-[#2f7cff]/50 bg-[#2f7cff] text-white shadow-[0_0_24px_rgba(47,124,255,0.55)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
      >
        <ChevronRight className="size-4" />
      </button>
      <div className="rounded-[22px] border border-white/10 bg-black/25 p-3 shadow-[0_0_0_1px_rgba(47,124,255,0.08),0_20px_80px_rgba(0,0,0,0.45)] backdrop-blur-md">
        <div ref={scroller} className="flex gap-3 overflow-x-auto scroll-smooth [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {products.map((product, index) => (
            <Link
              key={product.id}
              href={`/products/${product.urlId}`}
              className="group relative h-[146px] w-[196px] shrink-0 overflow-hidden rounded-xl border border-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#2f7cff]"
              style={index === 0 ? { boxShadow: "0 0 0 1.5px rgba(80,160,255,1), 0 0 32px rgba(47,124,255,0.7)" } : undefined}
            >
              {product.bannerImage ? (
                <Image src={product.bannerImage} alt="" fill sizes="196px" className="object-cover transition duration-500 group-hover:scale-105" />
              ) : (
                <span className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,#16315f,transparent_55%),linear-gradient(160deg,#0c1424,#05070c)]" />
              )}
              <span className="absolute inset-0 bg-gradient-to-t from-black via-black/25 to-transparent" />
              <span className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-2 p-3">
                <span>
                  <span className="block text-[11px] font-bold tracking-[0.14em] text-white uppercase">{product.name}</span>
                  <span className="mt-0.5 block text-[10px] text-white/60">{product.category || typeLabel[product.productType]}</span>
                </span>
                <ArrowUpRight className="mb-0.5 size-3.5 shrink-0 text-white/80" />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
